import { useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import {
  ArrowRight,
  ShieldCheck,
  UserPlus,
} from 'lucide-react'
import { useAuth } from '@/context/auth-context'
import { getInvitePublicDetails } from '@/api/apis/org/invite'
import { PageLoader } from '@/components/page-loader'
import AnimatedButton from '@/components/ui/animated-button'
import { Card } from '@/components/ui/card'
import config from '@/config/config.env'

export const Route = createFileRoute('/accept-invite')({
  component: AcceptInvite,
})

function AcceptInvite() {
  const { id } = Route.useSearch() as { id: string }
  const { isLoggedIn, loading: authLoading } = useAuth()
  
  const [inviteData, setInviteData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [safetyCleared, setSafetyCleared] = useState(false)

  // Safety Timeout for Auth Loader
  useEffect(() => {
    const timer = setTimeout(() => {
      setSafetyCleared(true)
    }, 2000)
    return () => clearTimeout(timer)
  }, [])

  // 1. Fetch public invitation details & Persist Intent
  useEffect(() => {
    if (!id) {
      setError('Missing invitation ID')
      setLoading(false)
      return
    }

    const fetchInvite = async () => {
      try {
        // INDUSTRY TOP-NOTCH: Save the intent here so AuthContext can auto-join later
        sessionStorage.setItem('pending_invite_id', id)
        
        const response = await getInvitePublicDetails(id)
        setInviteData(response.data)
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load invitation')
      } finally {
        setLoading(false)
      }
    }

    fetchInvite()
  }, [id])

  const handleLoginRedirect = () => {
    const forwardUrl = encodeURIComponent(`${window.location.pathname}${window.location.search}`)
    window.location.href = `${config.urls.authUrl}/login?forward=${forwardUrl}&portal=recruiter`
  }

  // Determine if we should show the loader
  const isAuthLoading = authLoading && !safetyCleared
  const shouldShowLoader = loading || (isLoggedIn && !error) || isAuthLoading

  if (shouldShowLoader) {
    return <PageLoader color='hsl(var(--primary))' />
  }

  if (error) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-slate-50 p-4 dark:bg-slate-900'>
        <Card className='w-full max-w-md p-8 text-center shadow-xl'>
          <h1 className='mb-4 text-2xl font-bold text-slate-900 dark:text-white'>Invitation Error</h1>
          <p className='mb-6 text-slate-600 dark:text-slate-400'>{error}</p>
          <AnimatedButton onClick={() => window.location.href = '/'}>
            Back to Home
          </AnimatedButton>
        </Card>
      </div>
    )
  }

  return (
    <div className='relative flex min-h-screen items-center justify-center overflow-hidden bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-50 via-slate-50 to-indigo-50 p-4 dark:from-slate-950 dark:via-slate-900 dark:to-indigo-950/30'>
      {/* Background decoration */}
      <div className='absolute -top-40 -right-40 h-96 w-96 rounded-full bg-primary/5 blur-3xl' />
      <div className='absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-indigo-500/5 blur-3xl' />
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className='relative z-10 w-full max-w-lg'
      >
        <Card className='overflow-hidden border-white/20 bg-white/70 p-8 shadow-2xl ring-1 ring-slate-200/50 backdrop-blur-2xl dark:bg-slate-900/70 dark:ring-slate-700/50'>
          <div className='mb-8 flex flex-col items-center text-center'>
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
              className='relative mb-6'
            >
              <div className='h-24 w-24 overflow-hidden rounded-2xl bg-slate-900 p-1 shadow-2xl dark:bg-slate-800'>
                {inviteData?.org_logo ? (
                  <img src={inviteData.org_logo} alt={inviteData.org_name} className='h-full w-full object-cover rounded-xl' />
                ) : (
                  <div className='flex h-full w-full items-center justify-center bg-gradient-to-br from-slate-800 to-slate-950 text-3xl font-bold text-white uppercase'>
                    {inviteData?.org_name?.charAt(0)}
                  </div>
                )}
              </div>
              <div className='absolute -bottom-2 -right-2 flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500 text-white shadow-lg ring-4 ring-white dark:ring-slate-900'>
                <ShieldCheck size={16} />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <span className='mb-2 inline-block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400'>
                Team Invitation
              </span>
              <h1 className='mb-3 text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white lg:text-4xl'>
                Join {inviteData?.org_name}
              </h1>
              <p className='mx-auto max-w-xs text-sm leading-relaxed text-slate-600 dark:text-slate-400'>
                You've been invited to join the team as a <span className='font-bold text-slate-900 dark:text-slate-200'>{inviteData?.role || 'member'}</span>. 
                Secure your workspace and start collaborating today.
              </p>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className='space-y-4'
          >
            <AnimatedButton
              className='h-14 w-full text-lg font-semibold'
              onClick={handleLoginRedirect}
              icon={<ArrowRight className='transition-transform group-hover:translate-x-1' />}
            >
              <UserPlus size={20} />
              Sign in to Join Team
            </AnimatedButton>
            
            <p className='text-center text-[11px] text-slate-500 dark:text-slate-500'>
              By joining, you agree to the team's shared workspace policies.
            </p>
          </motion.div>
        </Card>
      </motion.div>
    </div>
  )
}
